import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  FormGroup,
  TextField,
  Box,
} from '@mui/material'
import { Question } from '../../types/test'

interface QuestionCardProps {
  question: Question
  questionNumber: number
  answer: string
  onAnswerChange: (questionId: string, answer: string) => void
  disabled?: boolean
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  questionNumber,
  answer,
  onAnswerChange,
  disabled = false,
}) => {
  const { t } = useTranslation('test')

  const handleSingleChoiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onAnswerChange(question.id, event.target.value)
  }

  const handleMultipleChoiceChange = (optionIndex: number, checked: boolean) => {
    const currentAnswers = answer ? answer.split(',').map(Number) : []
    let newAnswers: number[]

    if (checked) {
      newAnswers = [...currentAnswers, optionIndex].sort()
    } else {
      newAnswers = currentAnswers.filter((index) => index !== optionIndex)
    }

    onAnswerChange(question.id, newAnswers.join(','))
  }

  const handleEssayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onAnswerChange(question.id, event.target.value)
  }

  const renderQuestionInput = () => {
    switch (question.questionType) {
      case 'SINGLE_CHOICE':
        return (
          <FormControl component="fieldset" fullWidth>
            <RadioGroup value={answer || ''} onChange={handleSingleChoiceChange}>
              {question.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={index.toString()}
                  control={<Radio disabled={disabled} />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )

      case 'MULTIPLE_CHOICE':
        const selectedAnswers = answer ? answer.split(',').map(Number) : []
        return (
          <FormControl component="fieldset" fullWidth>
            <FormGroup>
              {question.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={selectedAnswers.includes(index)}
                      onChange={(e) => handleMultipleChoiceChange(index, e.target.checked)}
                      disabled={disabled}
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
          </FormControl>
        )

      case 'TRUE_FALSE':
        return (
          <FormControl component="fieldset" fullWidth>
            <RadioGroup value={answer || ''} onChange={handleSingleChoiceChange}>
              <FormControlLabel
                value="0"
                control={<Radio disabled={disabled} />}
                label={t('question.trueFalse.true')}
              />
              <FormControlLabel
                value="1"
                control={<Radio disabled={disabled} />}
                label={t('question.trueFalse.false')}
              />
            </RadioGroup>
          </FormControl>
        )

      case 'ESSAY':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={answer || ''}
            onChange={handleEssayChange}
            disabled={disabled}
            placeholder={t('question.essayPlaceholder')}
          />
        )

      default:
        return <Typography color="error">{t('question.unsupportedType')}</Typography>
    }
  }

  const getQuestionTypeLabel = (type: string) => {
    switch (type) {
      case 'SINGLE_CHOICE':
        return t('question.types.SINGLE_CHOICE')
      case 'MULTIPLE_CHOICE':
        return t('question.types.MULTIPLE_CHOICE')
      case 'TRUE_FALSE':
        return t('question.types.TRUE_FALSE')
      case 'ESSAY':
        return t('question.types.ESSAY')
      default:
        return type
    }
  }

  return (
    <Card sx={{ mb: 3, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={2}>
          <Typography
            variant="h6"
            component="h3"
            sx={{
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            {t('question.questionNumber', { number: questionNumber })}
          </Typography>
          <Box
            textAlign="right"
            sx={{
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            <Typography variant="caption" color="textSecondary" display="block">
              {getQuestionTypeLabel(question.questionType)}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              {t('question.points', { points: question.points })}
            </Typography>
          </Box>
        </Box>

        <Typography variant="body1" paragraph>
          {question.questionText}
        </Typography>

        {renderQuestionInput()}

        {question.questionType === 'MULTIPLE_CHOICE' && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            {t('question.multipleChoiceHint')}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}
